import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Button, Tooltip, Zoom, useTheme, useMediaQuery } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { UserReportModal } from "./UserReportModal";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { IApplicationState } from "@/store-redux";
import { ERole } from "@/common";
import { KeyTokenName } from "@/constants";

const STORAGE_KEY_POS = "ffl_bug_report_btn_pos";

interface Position {
  x: number;
  y: number;
}

export const UserReportWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const buttonRef = useRef<HTMLDivElement | null>(null);
  const dragInfoRef = useRef<{
    startX: number;
    startY: number;
    initX: number;
    initY: number;
    hasMoved: boolean;
  }>({
    startX: 0,
    startY: 0,
    initX: 0,
    initY: 0,
    hasMoved: false,
  });

  const currentUser = useSelector(
    (state: IApplicationState) => state.currentUser,
  );
  const token = localStorage.getItem(KeyTokenName);

  const userRole = currentUser?.data?.role;
  const isAdminRole = userRole === ERole.ADMIN || userRole === ERole.SUPER_ADMIN;
  const isCmsRoute =
    location.pathname.startsWith("/cms") ||
    location.pathname.startsWith("/admin");

  const isPublicAuthPage =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/register") ||
    location.pathname.startsWith("/intro");

  const isAuthenticated = !!token || (currentUser && currentUser.data?._id);

  // Giới hạn tọa độ luôn nằm bên trong màn hình
  const clampPosition = useCallback((x: number, y: number): Position => {
    const btnWidth = buttonRef.current?.offsetWidth || (isMobile ? 50 : 160);
    const btnHeight = buttonRef.current?.offsetHeight || 44;
    const padding = 12;

    const maxX = window.innerWidth - btnWidth - padding;
    const maxY = window.innerHeight - btnHeight - padding;

    return {
      x: Math.max(padding, Math.min(x, maxX)),
      y: Math.max(padding, Math.min(y, maxY)),
    };
  }, [isMobile]);

  // Khởi tạo vị trí từ localStorage hoặc mặc định góc dưới bên phải
  useEffect(() => {
    try {
      const savedPos = localStorage.getItem(STORAGE_KEY_POS);
      if (savedPos) {
        const parsed = JSON.parse(savedPos);
        if (typeof parsed.x === "number" && typeof parsed.y === "number") {
          setPosition(clampPosition(parsed.x, parsed.y));
          return;
        }
      }
    } catch (e) {
      console.warn("Could not parse saved bug report button position:", e);
    }

    // Mặc định góc dưới phải
    const defaultX = window.innerWidth - (isMobile ? 65 : 175);
    const defaultY = window.innerHeight - 80;
    setPosition(clampPosition(defaultX, defaultY));
  }, [clampPosition, isMobile]);

  // Lắng nghe resize cửa sổ để tự động kéo nút vào trong nếu bị tràn
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => {
        if (!prev) return prev;
        return clampPosition(prev.x, prev.y);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampPosition]);

  // Bắt đầu kéo thả (Mouse / Touch)
  const handleStartDrag = (clientX: number, clientY: number) => {
    if (!position) return;
    dragInfoRef.current = {
      startX: clientX,
      startY: clientY,
      initX: position.x,
      initY: position.y,
      hasMoved: false,
    };

    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
      const curX = "touches" in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const curY = "touches" in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;

      const dx = curX - dragInfoRef.current.startX;
      const dy = curY - dragInfoRef.current.startY;

      if (!dragInfoRef.current.hasMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        dragInfoRef.current.hasMoved = true;
        setIsDragging(true);
      }

      if (dragInfoRef.current.hasMoved) {
        const nextPos = clampPosition(
          dragInfoRef.current.initX + dx,
          dragInfoRef.current.initY + dy,
        );
        setPosition(nextPos);
      }
    };

    const handleEnd = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);

      if (dragInfoRef.current.hasMoved) {
        setPosition((latestPos) => {
          if (latestPos) {
            localStorage.setItem(STORAGE_KEY_POS, JSON.stringify(latestPos));
          }
          return latestPos;
        });

        setTimeout(() => {
          setIsDragging(false);
          dragInfoRef.current.hasMoved = false;
        }, 80);
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Chỉ nhận chuột trái
    handleStartDrag(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      handleStartDrag(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (dragInfoRef.current.hasMoved || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setIsOpen(true);
  };

  if (
    !isAuthenticated ||
    isPublicAuthPage ||
    !position ||
    isAdminRole ||
    isCmsRoute
  ) {
    return null;
  }

  return (
    <>
      <Zoom in={true} timeout={300}>
        <Box
          id="user-report-widget-container"
          ref={buttonRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onClick={handleClick}
          sx={{
            position: "fixed",
            left: `${position.x}px`,
            top: `${position.y}px`,
            zIndex: 1300,
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
            touchAction: "none",
            transition: isDragging ? "none" : "transform 0.15s ease-out",
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <Tooltip
            title="Kéo thả để di chuyển vị trí • Bấm để báo lỗi & góp ý"
            placement="top"
            arrow
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<BugReportIcon sx={{ fontSize: 19 }} />}
              sx={{
                borderRadius: "100px",
                px: isMobile ? 1.5 : 2,
                py: 1,
                minWidth: isMobile ? "44px" : "auto",
                fontWeight: 800,
                fontSize: 13,
                textTransform: "none",
                boxShadow: isDragging
                  ? "0 16px 36px rgba(249, 115, 22, 0.65)"
                  : "0 8px 24px rgba(249, 115, 22, 0.45), 0 2px 6px rgba(225, 29, 72, 0.25)",
                background: "linear-gradient(135deg, #FF6B35 0%, #F97316 50%, #E11D48 100%)",
                color: "#FFFFFF",
                border: "1.5px solid rgba(255, 255, 255, 0.35)",
                backdropFilter: "blur(10px)",
                transform: isDragging ? "scale(1.06)" : "scale(1)",
                transition: "box-shadow 0.2s, transform 0.2s, background 0.2s",
                pointerEvents: "auto",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
                "&:hover": {
                  boxShadow: "0 12px 32px rgba(249, 115, 22, 0.6), 0 4px 10px rgba(225, 29, 72, 0.4)",
                  background: "linear-gradient(135deg, #FF5722 0%, #EA580C 50%, #BE123C 100%)",
                  transform: "scale(1.04)",
                },
              }}
            >
              {!isMobile && "Báo lỗi / Góp ý"}
              {!isMobile && (
                <DragIndicatorIcon
                  sx={{
                    fontSize: 15,
                    ml: 0.5,
                    opacity: 0.7,
                    color: "rgba(255, 255, 255, 0.8)",
                  }}
                />
              )}
            </Button>
          </Tooltip>
        </Box>
      </Zoom>

      <UserReportModal open={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default UserReportWidget;
